import { useEffect } from "react";

/**
 * Was auf dieser Seite steht — für Maschinen, nicht für Augen.
 *
 * Unter `/ai` steht, wie die API insgesamt funktioniert. Das beantwortet
 * aber nicht die Frage, vor der ein Agent auf *dieser* Seite steht: was ist
 * hier zu sehen, und über welchen Aufruf käme er an dieselben Daten? Genau
 * das steht hier — einmal als Satz und einmal als JSON-LD.
 *
 * Zweimal absichtlich: der Satz wird auch von einem Modell verstanden, das
 * nur den Text der Seite bekommt; das JSON-LD von allem, was Struktur
 * erwartet. Beides ist unsichtbar — der Kasten steht neben dem Bildausschnitt
 * und ist für Vorlesehilfen ausgeblendet (ein Mensch mit Screenreader will
 * keine API-Dokumentation vorgelesen bekommen), das Skript rendert der
 * Browser ohnehin nie.
 */
export function AiNotiz({
  titel, beschreibung, endpunkte, daten,
}: {
  /** Worum es auf der Seite geht — eine Zeile. */
  titel: string;
  /** Zwei, drei Sätze: was hier steht und was man damit tun kann. */
  beschreibung: string;
  /** Die Aufrufe, die dieselben Daten liefern. */
  endpunkte: { pfad: string; zweck: string }[];
  /** Optional: die Einträge selbst, damit ein Agent nicht erst raten muss,
   *  wonach er sucht. */
  daten?: Record<string, unknown>;
}) {
  // Das JSON-LD hängt am <head>, nicht im Baum: React würde den Inhalt eines
  // <script> sonst als Text escapen, und aus den Anführungszeichen im JSON
  // würde &quot; — unlesbar für jeden Parser.
  useEffect(() => {
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.dataset.ai = "seite";
    el.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: titel,
      description: beschreibung,
      potentialAction: endpunkte.map((e) => ({
        "@type": "SearchAction",
        target: `${location.origin}/api${e.pfad}`,
        description: e.zweck,
      })),
      ...(daten ?? {}),
    }, null, 2);
    document.head.appendChild(el);
    return () => { el.remove(); };
  }, [titel, beschreibung, endpunkte, daten]);

  return (
    <div className="nur-fuer-maschinen" aria-hidden="true" data-ai="hinweis">
      <p>{titel} — {beschreibung}</p>
      <p>
        The same data over the API (JSON, no sign-in needed for what is public):
      </p>
      <ul>
        {endpunkte.map((e) => (
          <li key={e.pfad}><code>GET /api{e.pfad}</code> — {e.zweck}</li>
        ))}
      </ul>
      <p>
        A full description of the API is at <a href="/ai">/ai</a>. Anything not
        listed as public needs a sign-in; do not guess at endpoints.
      </p>
    </div>
  );
}
